// Mistake tagger for a single closed trade. Edgewonk-style post-mortem.
// Use as an inline editor on history rows or a popover.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertOctagon, Save } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

type Props = { orderId: number };

export function MistakeTagger({ orderId }: Props) {
  const qc = useQueryClient();
  const tagsQ = useQuery({ queryKey: ['mistake-tags'], queryFn: api.mistakeTags });
  const dataQ = useQuery({
    queryKey: ['mistakes-for', orderId],
    queryFn: () => api.getMistakesFor(orderId),
  });

  const [tags, setTags] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [initialised, setInitialised] = useState(false);

  useEffect(() => {
    if (dataQ.data && !initialised) {
      setTags(dataQ.data.tags);
      setNote(dataQ.data.note);
      setInitialised(true);
    }
  }, [dataQ.data, initialised]);

  const save = useMutation({
    mutationFn: () => api.putMistakes(orderId, { tags, note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mistakes'] });
      qc.invalidateQueries({ queryKey: ['mistakes-for', orderId] });
      toast.success('Mistakes saved');
    },
    onError: () => toast.error('Save failed'),
  });

  const toggle = (t: string) => {
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5 space-y-2">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-400">
        <AlertOctagon className="size-3 text-amber-400" /> Post-mortem tags
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(tagsQ.data?.tags ?? []).map((t) => (
          <button
            type="button"
            key={t}
            onClick={() => toggle(t)}
            aria-pressed={tags.includes(t)}
            className={cn(
              'text-xs px-2.5 py-1.5 rounded-full border transition-colors min-h-[28px]',
              tags.includes(t)
                ? 'border-rose-500/50 bg-rose-500/15 text-rose-200'
                : 'border-zinc-800 text-zinc-400 hover:text-zinc-200',
            )}
          >
            {t}
          </button>
        ))}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What would I do differently next time?"
        rows={3}
        className="w-full bg-zinc-950 border border-zinc-800 rounded p-2 text-xs text-zinc-100 outline-none focus:border-indigo-500/60 placeholder:text-zinc-400 resize-none"
      />
      <button
        type="button"
        onClick={() => save.mutate()}
        disabled={save.isPending}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-indigo-500 text-white hover:bg-indigo-400 disabled:bg-zinc-800 disabled:text-zinc-400 min-h-[32px]"
      >
        <Save className="size-3.5" /> {save.isPending ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}
